import { APP_ENVS } from '@/shared/constants/envs';
import { APP_ROUTES } from '@/shared/constants/navigation';

import type { FaqEntry } from './landing-constants';

/**
 * Static content for /switch, the one page for the switching offer. Every
 * compare page's "Switch from X" CTA lands here. The offer: the months left on
 * the old ATS contract become free months on any Binary annual plan. Migration is
 * self-serve, via the AI agent, or team-handled on Pro. Matches the landing FAQ
 * ("What if I already have an ATS?").
 */

// Last content change. Bump only when the page content changes: it feeds the page's JSON-LD and its sitemap lastmod.
export const SWITCH_UPDATED = '2026-10-17';

/** How long a migration takes once we have your export. */
export const SWITCH_MIGRATION_HOURS = 48;

export const SWITCH_KEYWORDS = [
	'switch ATS',
	'ATS migration',
	'migrate ATS data',
	'change applicant tracking system',
	'switch ATS free',
	'ATS switching offer',
	'move to a new ATS',
];

/** Mailto for the switch request. Pre-filled subject so support can triage it. */
export const SWITCH_MAILTO = `mailto:${APP_ENVS.COMPANY_SUPPORT_EMAIL}?subject=${encodeURIComponent(
	`Switching to ${APP_ENVS.COMPANY_NAME}`
)}`;

/** The three steps, also published as schema.org HowTo. Sign-up comes first; the export comes once you are in. */
export const SWITCH_STEPS = [
	{
		title: 'Start for free and pick an annual plan',
		body: 'Sign up, set up your workspace and choose the annual plan that fits. Prefer a walkthrough first? Email us and we will set one up.',
	},
	{
		title: 'Tell us your contract end date',
		body: `Email ${APP_ENVS.COMPANY_SUPPORT_EMAIL} the end date of your current ATS contract. The months left on it become free months on your Binary plan, so you only pay for the rest of the year.`,
	},
	{
		title: `Migrate in ${SWITCH_MIGRATION_HOURS} hours`,
		body: 'Import your export yourself in minutes, ask the AI agent to do it, or on the Pro plan let our team move your jobs, candidates and history for you. Live roles keep moving while you do it.',
	},
];

export const SWITCH_FAQ_DATA: FaqEntry[] = [
	{
		question: 'How does the switching offer work?',
		answer:
			'If you switch while you still have time left on your current ATS contract, those months are free on Binary. Pick any annual plan, Starter, Business or Pro, and the months left on your old contract are credited as free months, so you pay for the rest of the year. Send us your contract end date and we confirm the free months before you move. It applies to any ATS, not just the ones we have comparison pages for.',
	},
	{
		question: `How long does an ATS migration take?`,
		answer: `About ${SWITCH_MIGRATION_HOURS} hours, and often much less. A self-serve import of your old ATS export takes minutes; a team-handled migration on the Pro plan is done within two days of us getting your file. Nothing in your old ATS is touched, so you can keep using it until you are settled in.`,
	},
	{
		question: 'Is the migration free?',
		answer:
			'Yes, on every plan. On Free, Starter and Business you import your export yourself in a few minutes from the dashboard, or ask the AI agent or an MCP client such as Claude to do it. On the Pro plan our team handles the whole migration for you. Either way, email us if you get stuck and we will help.',
		link: { href: APP_ROUTES.PRICING, match: 'Pro plan' },
	},
	{
		question: 'What data moves over?',
		answer:
			'Jobs, candidates, their stage in each pipeline, notes and attached resumes. Candidates are matched to the right job, so a role that is half-way through interviews picks up where it left off.',
	},
	{
		question: 'Which ATS can I switch from?',
		answer:
			'Any ATS that can export a spreadsheet or CSV, which is all of them. We migrate teams from Greenhouse, Ashby, Workable, Lever, Teamtailor, Dover, Freshteam, Zoho Recruit, Breezy HR, JazzHR, BambooHR, Rippling, Recruitee, Homerun, Keka and Recruit CRM most often, and have a side-by-side comparison for each.',
		link: { href: APP_ROUTES.COMPARE, match: 'side-by-side comparison' },
	},
	{
		question: 'What happens to candidates who are mid-process?',
		answer:
			'They move with their stage, notes and resume, and keep getting replies. Interview scheduling runs on your Google Calendar, so booked interviews are unaffected. Tell candidates nothing changes for them; they apply and reply the same way.',
	},
	{
		question: 'Can I switch on my own, or talk to someone first?',
		answer:
			'Either. You can start on the free plan today and import your data yourself, or email us and we will walk you through Binary and handle the migration with you. The switching offer applies to annual billing on any paid plan; if you would rather not commit for a year, monthly billing is available too.',
		link: { href: APP_ROUTES.FREE_ATS, match: 'free plan' },
	},
];

/** Intro under the "Switching from…" compare grid. One line: the block is centred. */
export const SWITCH_COMPARE_INTRO = [
	'Pick your current ATS for a side-by-side comparison and what changes when you move.',
];
